import type { CegoXmpp } from '@cego/xmpp'
import { createSharedComposable, shallowRef, useClock, useCoreLogger, useXmppStatus, useXmppConfig, useUserInfo } from '#imports'
import { useRuntimeConfig } from '#app'

export const useXmpp = createSharedComposable(() => {
    if (import.meta.server) {
        return {
            ensureInit: async () => undefined,
            getInstance: () => undefined,
        }
    }

    const config = useRuntimeConfig()
    const logger = useCoreLogger()
    const { setClock } = useClock()
    const { setStatus } = useXmppStatus()

    const instance = shallowRef<InstanceType<typeof CegoXmpp>>()
    let initPromise: Promise<InstanceType<typeof CegoXmpp> | undefined> | null = null

    function ensureInit(): Promise<InstanceType<typeof CegoXmpp> | undefined> {
        if (initPromise) return initPromise

        initPromise = _init() ?? Promise.resolve(undefined)
        return initPromise
    }

    function _init(): Promise<InstanceType<typeof CegoXmpp>> | undefined {
        if (!config.public.xmpp.service || !config.public.xmpp.domain) {
            return undefined
        }

        return Promise.all([
            import('@cego/xmpp'),
            useUserInfo(),
            useXmppConfig({ immediate: false }),
        ]).then(([{ CegoXmpp }, { data: user }, xmppConfig]) => {
            function getWebsocketNickname() {
                return user.value?.username || xmppConfig.data.value.jid
            }

            const xmpp = new CegoXmpp({
                service: config.public.xmpp.service,
                domain: config.public.xmpp.domain,
                maxListeners: 30,
                async credentials(auth: ({ username, password }: { username: string, password: string }) => void): Promise<void> {
                    await xmppConfig.refresh()

                    const { jid, password } = xmppConfig.data.value

                    return auth({ username: jid, password })
                },
                async onlineHandler() {
                    await xmpp.sendRoomPresence(config.public.core.site.brand, getWebsocketNickname())
                    await xmpp.sendRoomPresence('globalroom', getWebsocketNickname())

                    logger.info('[xmpp] Online detected')
                    setStatus('online')
                },
                offlineHandler() {
                    logger.info('[xmpp] Offline detected')
                    setStatus('offline')
                },
                errorHandler(err) {
                    logger.error(err)
                },
            })

            xmpp.start()

            xmpp.on({ kind: 'message', type: 'headline', xmlns: 'cego:ServerTimeUpdated' }, ({ json }: { json: any }) => {
                setClock(json.serverTime)
            })

            xmpp.on({ kind: 'message', type: 'error' }, ({ error }) => {
                console.error(error)
            })

            instance.value = xmpp

            return xmpp
        })
    }

    function getInstance(): InstanceType<typeof CegoXmpp> | undefined {
        return instance.value
    }

    return {
        ensureInit,
        getInstance,
    }
})
